import useDocsActiveHeading from "@/contexts/DocsActiveHeadingContext";
import { useEffect, useRef } from "react";

type Props = {
  heading: string;
  anchorSuitableId: string;
  visible?: boolean;
};

/** 
 * H3 Heading for documentation anchors.
 * Uses intersection observer with useDocsActiveHeading to set active
 * state when the heading scrolls into view.
 */
const AnchorHeading = ({
  heading,
  anchorSuitableId,
  visible = true,
}: Props) => {
  const { setId } = useDocsActiveHeading();
  const element = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const handleObserver = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry?.isIntersecting) {
          setId(entry.target.id);
        }
      });
    };
    const observer = new IntersectionObserver(handleObserver, {
      // Uses minimal window of viewport to avoid multiple headings
      // trying to appear as active at same time.
      rootMargin: "-20% 0px -40% 0px",
      threshold: 1,
    });

    if (element.current) {
      observer.observe(element.current);
    }

    return () => observer.disconnect();
  }, [setId]);

  return (
    <h3
      id={anchorSuitableId}
      ref={element}
      aria-hidden={!visible || undefined}
      style={
        visible
          ? undefined
          : { height: "1px", margin: 0, overflow: "hidden", padding: 0 }
      }
    >
      {visible ? heading : null}
    </h3>
  );
};

export default AnchorHeading;
